import { ApiParameter, get } from "@insite/client-framework/Services/ApiService";
import { CategoryCollection } from "@insite/client-framework/Services/CategoryService";
import { ClickableProps } from "@insite/mobius/Clickable";

export interface GetCategoriesApiParameter extends ApiParameter {
    maxDepth?: number;
    startCategoryId?: string;
    includeStartCategory?: boolean;
}

export interface CustomCategoryCollectionModel extends CategoryCollection {
    categories: MappedLink[];
}

export interface ProductDropdownCategoryLevel01 {
    name: string;
    columns: string;
    url: string;
    categoriesLevel2: { categoryLevel2: ProductDropdownCategoryLevel02[] };
    additionalContent: ProductDropdownCategoryAdditional;
    rows?: any[][];
}

export interface ProductDropdownCategoryLevel02 {
    name: string;
    url: string;
    openInNewTab: string;
    column: string;
    categoriesLevel3: { categoryLevel3: ProductDropdownCategoryLevel03[] };
}

export interface ProductDropdownCategoryLevel03 {
    name: string;
    url: string;
    openInNewTab: string;
}

export interface ProductDropdownCategoryAdditional {
    title: string;
    links: { link: ProductDropdownCategoryAdditionalLink[] };
}

export interface ProductDropdownCategoryAdditionalLink {
    name: string;
    url: string;
    imageUrl: string;
    openInNewTab: string;
    showAsImage: string;
}

export interface MappedLink {
    title: string;
    url?: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    // Verndale Customization
    columns?: string;
    column?: string;
    openInNewTab?: boolean;
    mainNavigationColumns?: MappedLink[][];
    additionalContent?: {
        links: { link: ProductDropdownCategoryAdditionalLink[] };
        title: string;
    } | null;
    clickableProps?: ClickableProps;
    level?: number;
}

const customCategoriesUrl = "api/v1/ProductDropdownMenu/categories";

export async function getCustomCategories(parameter: GetCategoriesApiParameter) {
    // we don't need to do anything with the parameter, but we need to pass it to the api

    const customCategoryCollectionModel = await get<CustomCategoryCollectionModel>(
        `${customCategoriesUrl}/`,
        parameter,
    );

    // we save the result customCategoryCollectionModel  in session storage so we can use it later
    const dateSaved = new Date();
    const dateSavedFormatted =
        dateSaved.getFullYear() +
        (dateSaved.getMonth() + 1).toString().padStart(2, "0") +
        dateSaved.getDate().toString().padStart(2, "0");

    // we format the customCategoryCollectionModel to the MappedLink format
    const mappedLinks: MappedLink[] = [];
    const mappedLinkChildren: MappedLink[] = [];

    if (customCategoryCollectionModel.categories && "categoryLevel1" in customCategoryCollectionModel.categories) {
        const { categoryLevel1 } = customCategoryCollectionModel.categories;

        (categoryLevel1 as ProductDropdownCategoryLevel01[]).forEach((category: ProductDropdownCategoryLevel01) => {
            const {
                name,
                url,
                categoriesLevel2: { categoryLevel2 },
                additionalContent,
                columns,
            } = category;
            const finalColumnItems: MappedLink[][] = [];
            let columnItems = [] as MappedLink[];
            let columnStart = "1";

            const categoryLevel2ToMappedLinks = categoryLevel2.map(item => {
                // we transform the categoryLevel2 to a MappedLink
                const { name, url, categoriesLevel3, column, openInNewTab } = item;
                let categoryLevel2ToMappedLink = {
                    title: name,
                    url,
                    column,
                    openInNewTab: openInNewTab === "Y",
                    children: [],
                } as MappedLink;

                if (categoriesLevel3 && categoriesLevel3.categoryLevel3.length > 0) {
                    categoryLevel2ToMappedLink = {
                        title: name,
                        url,
                        column,
                        openInNewTab: openInNewTab === "Y",
                        children: categoriesLevel3.categoryLevel3.map(item => {
                            // we transform the categoryLevel3 to a MappedLink
                            const { name, url, openInNewTab } = item;
                            return {
                                title: name,
                                url,
                                openInNewTab: openInNewTab === "Y",
                                level: 3,
                            };
                        }),
                        level: 2,
                    };
                }

                if (column) {
                    // we separate the grand children into columns 1 to 4
                    // based on the column property

                    if (column !== columnStart) {
                        columnStart = column;
                        finalColumnItems.push(columnItems);
                        columnItems = [];
                    }

                    columnItems.push(categoryLevel2ToMappedLink);
                }

                return categoryLevel2ToMappedLink;
            });

            finalColumnItems.push(columnItems);

            const mappedLink: MappedLink = {
                title: name,
                url,
                type: "Category",
                childrenType: "MegaMenu",
                children: categoryLevel2ToMappedLinks,
                numberOfColumns: +columns,
                additionalContent: additionalContent || null,
                mainNavigationColumns: finalColumnItems,
                level: 1,
            };

            mappedLinkChildren.push(mappedLink);
        });

        mappedLinks.push({
            title: "Products",
            url: "",
            type: "Category",
            childrenType: "MegaMenu",
            children: mappedLinkChildren,
            numberOfColumns: 4,
        });
    }

    const result: CustomCategoryCollectionModel = {
        ...customCategoryCollectionModel,
        categories: [...mappedLinks],
    };

    sessionStorage.setItem("customCategoryCollectionModel", JSON.stringify(result));
    sessionStorage.setItem("customCategoryCollectionModelDate", dateSavedFormatted);

    return result;
}
